<template lang="pug">
    Panel(panelClassName="builder-criteria")
        //- HEADER
        template(slot="header")
            sectionHeader(title="criteria")
                template(slot="action")
                    .section-header--action
                        button.btn.btn-simple(
                            :class="{ 'is-disabled': !criteriaListIsReady }"
                            @click="exportDataForm('segment')"
                        ) Export data

        template(slot="content")
            .criteria-main
                //- SEARCH
                .search-criteria-wrapper
                    input.search-criteria(
                        :class="{ 'isFocus': searchForm.query !== '' }"
                        placeholder="Search criterion"
                        v-model.trim="searchForm.query"
                        @keyup.esc="resetSearch()"
                        @keyup.enter="launchSearch()")
                    svg-icon.search-reset(
                        name='close'
                        size='small'
                        @click.native="resetSearch()"
                        v-if="searchForm.query !== ''"
                    )
                    .search-filter
                        | Filter on :
                        select.search-targets(v-model="searchForm.target")
                            option(
                                v-for="item in searchForm.targetsOpts"
                                :key="item.value"
                                :value="item.value"
                            ) {{ item.label }}

                //- CATEGORIES
                template(v-if="stillLoading")
                    Spinner.l-flex-row--center.l-align-center(
                        width="50"
                        height="50"
                    )
                .wrapper-criteria-list(v-if="!isFiltered")
                    .criteria-categories.scrollbar.is-scrollable.is-horizontally-scrollable(v-if="currentPath.length === 0")
                        .category-list(
                            v-for="(subCategories, categoryLabel) in categoriesTree"
                            :key="categoryLabel"
                        )
                            .category
                                .category-label {{ categoryLabel }}
                                .category-item(
                                    v-for="(criteria, subCategoryLabel) in subCategories"
                                    :key="subCategoryLabel"
                                    @click.stop="selectCriteria([categoryLabel, subCategoryLabel])"
                                ) {{ subCategoryLabel }}

                    template(v-else)
                        //- BREADCRUMBS
                        .breadcrumb.l-flex-row.l-align-center
                            svg-icon.breadcrumb-path-root(
                                name='home'
                                size='small'
                                @click.native="unSelectCriteria()"
                            )
                            .l-flex-row.breadcrumb-path
                                .breadcrumb-path-item(
                                    v-for="(label, index) in currentPath"
                                    :key="index"
                                    :title="label"
                                    @click="unSelectCriteria()"
                                ) {{ label }}


                        //- TREE
                        .tree-view
                            CriteriaListTree(
                                class="criteria-tree-container scrollbar is-scrollable is-horizontally-scrollable"
                                :nodes="selectedCriteria"
                                :formula="formula"
                                @onTagClick="searchTag"
                            )

                //- SEARCH RESULT LIST
                .search-result-wrapper.scrollbar.is-scrollable(v-if="isFiltered")
                    .no-results(v-if="filteredCriteriaList.length === 0") No results found
                    SearchResultList(
                        v-for="(item, index) in filteredCriteriaCategoriesTree"
                        :key="index"
                        :label="item.label"
                        :criteria="item.criteria"
                        :formula="formula"
                        @onTagClick="searchTag"
                    )
</template>

<script>
import { get, set } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'

// Components
import CriteriaListSpinner from './ListSpinner.vue'
import CriteriaListTree from './Tree'
import Panel from '@/components/common/Panel'
import Spinner from '@/components/common/Spinner'
import SearchResultList from './SearchResultList.vue'
import SectionHeader from '@/components/common/SectionHeader'
import { CustomSegment } from '@node/wam-audience-builder'

export default {
    name: 'CriteriaList',

    components: {
        CriteriaListSpinner,
        CriteriaListTree,
        Panel,
        SearchResultList,
        SectionHeader,
        Spinner
    },

    props: {
        formula: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            currentPath: [],
            searchForm: {
                query: '',
                target: 'label',
                targetsOpts: [
                    { label: 'Label', value: 'label' },
                    { label: 'Id', value: 'id' },
                    { label: 'Tag', value: 'tag' }
                ]
            },
            searchQuery: ''
        }
    },

    computed: {
        ...mapGetters('builder', ['criteriaList', 'criteriaListWithoutCustomTaxonomies', 'customTaxonomiesTree']),
        ...mapGetters('ui/builder', ['criteriaListIsReady']),
        ...mapGetters('data/customTaxonomies', ['builderIsLoading']),
        ...mapState('ui/builder', {
            pathSelected: state => state.formula.path
        }),

        isFiltered() {
            return this.searchQuery !== ''
        },

        stillLoading() {
            return !this.criteriaListIsReady || this.builderIsLoading
        },

        filteredCriteriaList() {
            if (this.searchQuery !== '') {
                return this.criteriaList.filter(criterion => {
                    if (!criterion.match) {
                        criterion = new CustomSegment(criterion)
                    }
                    return criterion.match(this.searchQuery, this.searchForm.target)
                })
            }
            return this.criteriaList
        },

        categoriesTree() {
            let tree = {}
            const key = Object.keys(this.customTaxonomiesTree)[0]

            if (key && this.customTaxonomiesTree[key]) {
                tree = {
                    [key]: {
                        ...this.customTaxonomiesTree[key],
                        ...(tree[key] ?? {}),
                    },
                    ...this.criteriaCategoriesTree
                }
            } else {
                return this.criteriaCategoriesTree
            }

            return tree
        },

        criteriaCategoriesTree() {
            const criterias = this.criteriaList.filter((criterion) => (
                !criterion.parentId && (
                    criterion.selectable ||
                    (
                        criterion.hasChildren ||
                        criterion.children.length > 0
                    )
                )
            ))
            const preparingCriterias = [...criterias].reduce((tree, criterion) => {
                const getPath = get(tree, criterion.path, [])
                const path = Array.isArray(getPath) ? getPath : [getPath]
                set(tree, criterion.path, [...path, criterion])
                return tree
            }, {})
            return preparingCriterias
        },

        filteredCriteriaCategoriesTree() {
            const list = []
            const { query, target } = this.searchForm
            const data = this.filteredCriteriaList.reduce((tree, criterion) => {
                const categoryLabel = criterion.path[0]
                tree[categoryLabel] = [...(tree[categoryLabel] || []), criterion]
                return tree
            }, {})

            Object.keys(data).forEach(item => {
                list.push({ label: item, criteria: data[item] })
            })

            // sort current path by label and loop through list to sort criteria data
            if (list.length > 0) {
                list.sort((a, _) => a.label.toLowerCase() === this.currentPath?.[0]?.toLowerCase()  ? -1 : 1)
                    .forEach((data) => {

                        // sort the second level by criteria path
                        if (this.currentPath?.[1]) {
                            data.criteria.sort((a, _) => a.path?.[1].toLowerCase() === this.currentPath?.[1].toLowerCase() ? -1 : 1)
                        }
                        // sort alphabetically for Audiences Type
                        if (data.label === 'Audiences' && target !== 'id') {
                            data.criteria.sort((a, b) => {
                                const labelA = a.label.toLowerCase()
                                const labelB = b.label.toLowerCase()
                                if (labelA < labelB) return -1
                                if (labelA > labelB) return 1
                                return 0
                            })
                        }
                        // sort by label tag if target equal to tag
                        if (target === 'tag') {
                            data.criteria.sort((a,b) => {
                                const tagA = a.tags.find(tag => tag.label.toLowerCase().includes(query.toLowerCase()))
                                const tagB = b.tags.find(tag => tag.label.toLowerCase().includes(query.toLowerCase()))
                                // sort alphabetically by tag if exist
                                if (tagA?.label.toLowerCase() < tagB?.label.toLowerCase()) return -1
                                if (tagA?.label.toLowerCase() > tagB?.label.toLowerCase()) return 1
                                return 0
                            })
                        }
                    })
            }
            return list
        },

        selectedCriteria() {
            const criteria = get(this.criteriaCategoriesTree, this.currentPath)
            return criteria.filter(a => a !== undefined && a.label).sort((a, b) => a.label.localeCompare(b.label))
        }
    },

    watch: {
        'searchForm.query': {
            handler(val) {
                if (val === '') this.resetSearch()
            }
        },
        pathSelected: {
            async handler(val) {
                const path = [...val]
                await this.selectCriteria(path.splice(0, 2))
                this.openCriteria(path[0])
            }
        }
    },

    methods: {
        ...mapMutations('exports', ['setExportForm']),
        ...mapMutations('ui/builder', ['openCriteria', 'selectPath']),
        exportDataForm(type) {
            this.setExportForm({
                display: true,
                type
            })
        },

        launchSearch() {
            this.searchQuery = this.searchForm.query
        },

        resetSearch() {
            this.searchForm.query = ''
            this.searchQuery = ''
        },

        searchTag(tag) {
            this.searchForm.query = tag
            this.searchForm.target = 'tag'
            this.searchQuery = tag
        },

        selectCriteria(path) {
            this.currentPath = path
        },

        unSelectCriteria() {
            this.currentPath = []
            if (this.pathSelected.length) {
                this.selectPath([])
            }
        }
    }
}
</script>
